import { useContext, useState } from "react";
import { cleanObject } from "../../../../helpers";
import { useGetInfo } from "../../../../hooks/useGetInfo";
import { useMutation, useQueryClient } from "react-query";
import {
  createService,
  updateServiceDetails,
} from "../../../../services/inventory";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContext } from "../../../../hooks/context";
import { pathConstants } from "../../../../routes/pathContants";
import { useMultipleImageUpload } from "../../../../pages/appPages/Inventory/SingleProducts/useMultipeImageUpload";

const reArrangeStoreProperties = (storeProperties, isEditMode = false) => {
  const newStoreProperties = [];
  storeProperties?.forEach((storeProperty) => {
    const newStoreProperty = {
      store_id: storeProperty?.store_id,
      pricings: storeProperty?.pricings?.map((pricingProperty) => {
        return {
          price: pricingProperty?.price,
          discount_rate: pricingProperty?.discount_rate,
          pricing_type_id: pricingProperty?.pricing_type?.id,
        };
      }),
      cost_price: `${isEditMode ? storeProperty?.cost_price || 0 : storeProperty?.cost_price || ""}`,
      discount_rate: `${storeProperty?.discount_rate || ""}`,
      returned_from_backend: true,
    };

    newStoreProperties.push(newStoreProperty);
  });

  return newStoreProperties;
};

export const useService = ({ productDetails = {}, isEditMode = false }) => {
  const params = useParams();
  const triggerToast = useContext(ToastContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const saveBookingConfig =
    productDetails?.store_service_properties?.[0]?.booking_config;
  const [rootFormDetails, setRootFormDetails] = useState({
    name: productDetails?.name || "",
    categories: [
      ...new Set(
        productDetails?.categories?.map(
          (category) => category?.parent_category?.id ?? category?.id,
        ) || [],
      ),
    ],
    sub_categories:
      productDetails?.categories
        ?.filter((category) => category?.parent_category)
        ?.map((category) => category.id) || [],
    show_product_in_sell_mode:
      productDetails?.show_product_in_sell_mode === false ? false : true,
    section: productDetails?.section || "",
    image_url: productDetails?.image_url || null,
    // vat: productDetails?.vat ? productDetails.vat : "",
    is_service: true,
    // vat_enabled: productDetails?.vat && productDetails.vat > 0 ? true : false,
    include_vat_in_price: productDetails?.include_vat_in_price,
    media_files: productDetails?.media_files || [],
    setup_duration: productDetails?.store_service_properties?.[0]
      ?.booking_config?.duration_value
      ? true
      : false,
    store_properties: reArrangeStoreProperties(
      productDetails?.["store_service_properties"],
      isEditMode,
    ),
    auto_create_product_on_squareup: false,
    booking_config: saveBookingConfig?.duration_value
      ? {
          ...(saveBookingConfig || {}),
          availability_slots: {
            ...(saveBookingConfig?.availability_slots?.[0] || {}),
            day_of_week: saveBookingConfig?.availability_slots?.map(
              (item) => item?.day_of_week,
            ),
          },
        }
      : {
          duration_value: "",
          duration_unit: "MINUTES",
          capacity: "",
          availability_slots: {
            day_of_week: [],
            start_time: "",
            end_time: "",
            ends_next_day: false,
          },
        },
  });

  const [detectedPricing, setDetectedPricing] = useState([]);
  const [modalViews, setModalViews] = useState({
    isCreatePricing: false,
    isCreateUnit: false,
    isWarning: false,
    isCreateSupplier: false,
  });
  const { getStoreInfo, getPricingInfo } = useGetInfo();

  const getBookingConfig = () => {
    if (!rootFormDetails?.setup_duration) return null;

    const dayOfWeek =
      rootFormDetails?.booking_config?.availability_slots?.day_of_week || [];

    const newAvailabilitySlots = dayOfWeek?.map((day) => {
      return {
        ...(rootFormDetails?.booking_config?.availability_slots || {}),
        day_of_week: day,
      };
    });

    return {
      ...(rootFormDetails?.booking_config || {}),
      availability_slots: newAvailabilitySlots,
    };
  };

  const { handleUploadMultipleMediaFiles } = useMultipleImageUpload();

  const handleCreateOrUpdateService = () => {
    let newDetectedPricing = [];

    const cleanSubmissionData = cleanObject(
      {
        ...rootFormDetails,
        stores: rootFormDetails?.store_properties?.map(
          (store_property) => store_property?.store_id,
        ),
      },
      ["booking_config"],
    );

    const bookingConfig = getBookingConfig();

    cleanSubmissionData.store_properties = [
      ...cleanSubmissionData?.store_properties,
    ]?.map((storeItem) => {
      const pricings = storeItem?.pricings?.map((pricingItem) => {
        if (Number(storeItem?.cost_price) > Number(pricingItem?.price))
          newDetectedPricing.push({
            store_name: storeItem?.store_name,
            store_currency: getStoreInfo(storeItem?.store_id)?.currency_code,
            cost_price: storeItem?.cost_price,
            selling_price: pricingItem?.price,
            pricing_name: getPricingInfo(pricingItem?.pricing_type_id)?.label,
          });

        return cleanObject(pricingItem, ["uuid"]);
      });

      return cleanObject({
        cost_price: storeItem?.cost_price,
        store_id: storeItem?.store_id,
        booking_config: bookingConfig,
        pricings,
      });
    });

    if (newDetectedPricing?.length > 0 && !modalViews?.isWarning) {
      setDetectedPricing(newDetectedPricing);
      return resetModalViews("isWarning", true);
    }

    resetModalViews("isWarning", false);

    if (isEditMode) return handleUpdateService(cleanSubmissionData);

    handleCreateService(cleanSubmissionData);
  };

  const { mutate: createNewService, isLoading: isCreatingService } =
    useMutation((data) => createService(data));
  const handleCreateService = (data) => {
    const { media_files, ...otherData } = data;
    createNewService(otherData, {
      onSuccess: (res) => {
        handleUploadMultipleMediaFiles({
          media_files: media_files,
          productId: res?.data?.data?.id,
          item_type: "service",
        });
        triggerToast("Service Created Sucessfully", "success");
        queryClient.invalidateQueries(["getProductList"]);
        navigate(pathConstants.INVENTORY_PRODUCTS);
      },
    });
  };

  const { mutate: updateService, isLoading: isUpdatingService } = useMutation(
    (data) => updateServiceDetails(data),
  );
  const handleUpdateService = (data) => {
    const { media_files = [], ...otherData } = data;
    updateService(
      { id: params?.id, data: otherData },
      {
        onSuccess: () => {
          handleUploadMultipleMediaFiles({
            media_files: media_files,
            productId: params?.id,
            item_type: "service",
          });
          triggerToast("Service Updated Sucessfully", "success");
          queryClient.invalidateQueries(["getServiceDetails", `${params?.id}`]);
          queryClient.invalidateQueries(["getProductList"]);
          navigate(-1);
        },
      },
    );
  };

  const resetModalViews = (key, value = true) => {
    setModalViews({
      isCreatePricing: false,
      isCreateUnit: false,
      isWarning: false,
      [key]: value,
    });
  };

  return {
    rootFormDetails,
    detectedPricing,
    modalViews,
    isLoadingService: isUpdatingService || isCreatingService,
    setRootFormDetails,
    resetModalViews,
    handleCreateOrUpdateService,
  };
};
